import React, { useEffect, useLayoutEffect, useState, useRef, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../utils/axiosInstance";
import { Button } from "@/components/ui/button";
import { PageLoader } from '@/components/ui/page-loader';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import {
  Upload,
  GripVertical,
  RefreshCw,
  Trash2,
  Search,
  Eye,
  Check,
  Lock,
  Palette,
  Zap,
} from "lucide-react";
import { BASE_URL } from "@/config/constants";
import { toast } from "sonner";
import { usePlan } from "@/context/PlanContext";
import PreviewContext from "@/context/PreviewContext";
import CustomDomainSettings from "@/components/CustomDomainSettings";
import ThemeCustomizerDrawer from "@/components/ThemeCustomizerDrawer";
import WebsiteLayoutEditor from "@/components/WebsiteLayoutEditor";
import CursorControlPanel from "@/components/CursorControlPanel";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface NavItem {
  name: string;
  href: string;
}

interface MasterTheme {
  _id: string;
  title: string;
  image: string;
  isPaid: boolean;
  price: number;
  allocatedDomains: string[];
  isActive: boolean;
  order: number;
}

interface UserTheme {
  _id: string;
  themeName: string;
  masterTheme: MasterTheme;
  domain_slug: string;
  isActive: boolean;
  order: number;
  customFonts?: Record<string, string>;
  customColors?: Record<string, string>;
  customFontWeight?: number | null;
  customBackgroundImage?: string | null;
  /** Per-section / per-role font-size scale multipliers: { [section]: { [role]: '1.2' } } */
  customSections?: Record<string, Record<string, string>>;
  /** Per-section background overrides: { [section]: { image?, video?, opacity } } (video: hero only) */
  customSectionBackgrounds?: Record<string, { image?: string | null; video?: string | null; opacity?: number }>;
}

interface SiteBranding {
  _id: string;
  siteName: string;
  tagline: string;
  loaderImage: string;
  logo: string;
  favicon: string;
  is_active: boolean;
  navItems: NavItem[];
}

interface User {
  _id: string;
  username: string;
  user_type: string;
  email: string;
  domain: string;
}
interface Domain {
  _id: string;
  slug: string;
  url: string;
  name?: string;
  customDomain?: string | null;
  dnsVerified?: boolean;
  /** 'artist' (default) | 'management' — management sites get roster/testimonials/clients/team sections. */
  siteType?: 'artist' | 'management';
}
interface SiteBrandingManagerProps {
  domain: string;
  user: User | null;
  domainObj:Domain
  setDomain: React.Dispatch<React.SetStateAction<Domain>>
}

// Sortable Nav Item Component
const SortableNavItem: React.FC<{
  id: string;
  index: number;
  item: NavItem;
  onNameChange: (value: string) => void;
  onHrefChange: (value: string) => void;
  readOnly: boolean;
}> = ({
  id,
  index,
  item,
  onNameChange,
  onHrefChange,
  readOnly,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center py-3 px-2 rounded-lg hover:bg-muted/30 transition-colors group"
    >
      <div className="flex items-center gap-2 sm:gap-3">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-muted-foreground transition-colors flex-shrink-0 p-1"
          title="Drag to reorder"
        >
          <GripVertical className="size-4" />
        </button>
        <span className="text-xs font-medium text-muted-foreground w-5 flex-shrink-0">
          {index + 1}
        </span>
      </div>
      <div className="flex flex-1 min-w-0 gap-2 sm:gap-3 pl-9 sm:pl-0">
        <div className="flex-1 min-w-0">
          <Input
            id={`nav-name-${index}`}
            type="text"
            value={item.name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Menu name"
            readOnly={readOnly}
          />
        </div>
        <div className="flex-1 min-w-0">
          <Input
            id={`nav-href-${index}`}
            type="text"
            value={item.href}
            onChange={(e) => onHrefChange(e.target.value)}
            placeholder="Link (e.g., #home)"
            readOnly={readOnly}
          />
        </div>
      </div>
    </div>
  );
};

const SiteBrandingManager: React.FC<SiteBrandingManagerProps> = ({
  domain,
  user,
  domainObj,
  setDomain: _setDomain
}) => {
  // console.log("Domain object in SiteBrandingManager:", domainObj);
  const previewContext = useContext(PreviewContext);
  const isPreviewOpen = previewContext?.isPreviewOpen ?? false;
  const isSuperAdmin = user?.user_type === "super-admin";
  const { capabilities, isPro, isItemVisibleOnWebsite, setShowUpgradeModal } = usePlan();
  // Router hooks — used to honor `location.state.brandingTab` so other pages
  // (e.g. the artist Dashboard's Theme / Custom Domain cards) can deep-link
  // directly into a specific inner tab of Website Settings.
  const location = useLocation();
  const navigate = useNavigate();
  const [branding, setBranding] = useState<SiteBranding | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "branding" | "navigation" | "theme" | "layout" | "cursor" | "customDomain"
  >("branding");
  const [activeAssetTab, setActiveAssetTab] = useState<
    "loader" | "logo" | "favicon"
  >("loader");
  const [form, setForm] = useState<Partial<SiteBranding>>({
    siteName: "",
    tagline: "",
    navItems: [],
  });
  const [uploadedImages, setUploadedImages] = useState<{
    loaderImage?: string;
    logo?: string;
    favicon?: string;
  }>({});
  const [originalForm, setOriginalForm] = useState<Partial<SiteBranding>>({
    siteName: "",
    tagline: "",
    navItems: [],
  });
  const [originalImages, setOriginalImages] = useState<{
    loaderImage?: string;
    logo?: string;
    favicon?: string;
  }>({});

  // Theme management state
  const [userThemes, setUserThemes] = useState<UserTheme[]>([]);
  const [themesLoading, setThemesLoading] = useState(false);
  const [themeSearchQuery, setThemeSearchQuery] = useState("");
  const [defaultFreeThemeIds, setDefaultFreeThemeIds] = useState<string[]>([]);
  const [activatingThemeId, setActivatingThemeId] = useState<string | null>(
    null,
  );

  // Theme customizer drawer state
  const [customizerOpen, setCustomizerOpen] = useState(false);
  const [customizerTarget, setCustomizerTarget] = useState<{
    userThemeId: string;
    themeTitle: string;
    isVariant: boolean;
    masterThemeTitle: string;
    savedFonts: Record<string, string>;
    savedColors: Record<string, string>;
    savedFontWeight: number | null;
    savedBackgroundImage: string | null;
    savedSections: Record<string, Record<string, string>>;
    savedSectionBackgrounds: Record<string, { image?: string | null; video?: string | null; opacity?: number }>;
  } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  // Theme card FLIP animation — track each card's DOM node and its last
  // known position so that when the grid reorders (e.g. the newly activated
  // theme jumps to slot #1) the cards glide to their new spots instead of
  // teleporting, and we can scroll the activated card into view.
  const themeCardRefs = useRef(new Map<string, HTMLDivElement>());
  const prevCardPositions = useRef(new Map<string, { top: number; left: number }>());

  // Shared motion constants so the card shuffle and the page scroll move in
  // lockstep — mismatched easings/durations are what makes it feel janky.
  const MOTION_DURATION = 550;
  const MOTION_EASING = "cubic-bezier(0.33, 1, 0.68, 1)"; // easeOutCubic
  const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

  // Document-relative layout position (offsetParent chain). Unlike
  // getBoundingClientRect this ignores both the current scroll offset and
  // any in-flight transform animations, so snapshots stay accurate — a
  // viewport-relative snapshot taken before a scroll would otherwise
  // produce phantom deltas and a bogus second shuffle on the next render.
  const getDocPos = (el: HTMLElement) => {
    let top = 0;
    let left = 0;
    let node: HTMLElement | null = el;
    while (node) {
      top += node.offsetTop;
      left += node.offsetLeft;
      node = node.offsetParent as HTMLElement | null;
    }
    return { top, left };
  };

  useLayoutEffect(() => {
    const newPositions = new Map<string, { top: number; left: number }>();
    themeCardRefs.current.forEach((el, id) => {
      newPositions.set(id, getDocPos(el));
    });
    newPositions.forEach((newPos, id) => {
      const oldPos = prevCardPositions.current.get(id);
      if (!oldPos) return;
      const dx = oldPos.left - newPos.left;
      const dy = oldPos.top - newPos.top;
      if (dx || dy) {
        themeCardRefs.current.get(id)?.animate(
          [
            { transform: `translate(${dx}px, ${dy}px)` },
            { transform: "translate(0, 0)" },
          ],
          { duration: MOTION_DURATION, easing: MOTION_EASING, composite: "replace" },
        );
      }
    });
    prevCardPositions.current = newPositions;
  });

  // Smoothly bring the activated card (now sorted to the front) into view
  // so the user doesn't feel lost after the reorder. We drive the scroll
  // ourselves with the same duration + easing as the FLIP so both motions
  // read as one coordinated movement (native smooth scroll has a
  // UA-defined curve that fights the card animation).
  const scrollToThemeCard = (userThemeId: string) => {
    requestAnimationFrame(() => {
      const el = themeCardRefs.current.get(userThemeId);
      if (!el) return;
      // Account for the fixed TopBar plus a little breathing room
      const TOP_OFFSET = 96;
      const targetY = Math.max(getDocPos(el).top - TOP_OFFSET, 0);
      const startY = window.scrollY;
      const delta = targetY - startY;
      if (Math.abs(delta) < 4) return;
      const startTime = performance.now();
      const step = (now: number) => {
        const t = Math.min((now - startTime) / MOTION_DURATION, 1);
        window.scrollTo(0, startY + delta * easeOutCubic(t));
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
  };

  // Setup DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Check if there are any changes
  const hasChanges =
    form.siteName !== originalForm.siteName ||
    form.tagline !== originalForm.tagline ||
    JSON.stringify(form.navItems) !== JSON.stringify(originalForm.navItems) ||
    uploadedImages.loaderImage !== originalImages.loaderImage ||
    uploadedImages.logo !== originalImages.logo ||
    uploadedImages.favicon !== originalImages.favicon;

  useEffect(() => {
    fetchBranding();
  }, [domain]);

  useEffect(() => {
    if (activeTab === "theme") {
      fetchUserThemes();
    }
  }, [activeTab, domain]);

  // Deep-link support: when navigated here with
  // `state: { brandingTab: '<tab>' }` (from Dashboard cards, sidebar quick
  // links, etc.), pre-select the requested inner tab and then clear the
  // state so a subsequent manual tab change isn't reverted on re-render.
  useEffect(() => {
    const requested = (location.state as { brandingTab?: string } | null)?.brandingTab;
    if (!requested) return;
    if (["branding", "navigation", "theme", "layout", "cursor", "customDomain"].includes(requested)) {
      setActiveTab(requested as "branding" | "navigation" | "theme" | "layout" | "cursor" | "customDomain");
    }
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Re-fetch themes when activated from preview panel
  const themeActivatedTrigger = previewContext?.themeActivatedTrigger || 0;
  const lastActivatedUserThemeId = previewContext?.lastActivatedUserThemeId || null;
  const prevThemeActivatedRef = useRef(themeActivatedTrigger);
  useEffect(() => {
    if (themeActivatedTrigger > prevThemeActivatedRef.current) {
      prevThemeActivatedRef.current = themeActivatedTrigger;
      // Optimistic update — immediately move the Active badge to the correct card
      if (lastActivatedUserThemeId) {
        setUserThemes((prev) =>
          prev.map((t) => ({ ...t, isActive: t._id === lastActivatedUserThemeId }))
        );
        scrollToThemeCard(lastActivatedUserThemeId);
      }
      fetchUserThemes(true); // silent background sync to confirm server state
    }
  }, [themeActivatedTrigger]);

  const fetchBranding = async () => {
    try {
      setLoading(true);
      const res = await axios.get(BASE_URL + "/api/site-branding");
      setBranding(res.data);
      const formData = {
        siteName: res.data.siteName,
        tagline: res.data.tagline,
        navItems: res.data.navItems || [],
      };
      const imagesData = {
        loaderImage: res.data.loaderImage,
        logo: res.data.logo,
        favicon: res.data.favicon,
      };
      setForm(formData);
      setOriginalForm(formData);
      setUploadedImages(imagesData);
      setOriginalImages(imagesData);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to load site branding";
      console.error("Fetch error:", err);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleNavItemChange = (
    index: number,
    field: "name" | "href",
    value: string,
  ) => {
    setForm((prev) => {
      const navItems = [...(prev.navItems || [])];
      navItems[index] = { ...navItems[index], [field]: value };
      return { ...prev, navItems };
    });
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setForm((prev) => {
        const navItems = [...(prev.navItems || [])];
        const activeIndex = navItems.findIndex(
          (_, i) => `nav-${i}` === active.id,
        );
        const overIndex = navItems.findIndex((_, i) => `nav-${i}` === over.id);

        if (activeIndex !== -1 && overIndex !== -1) {
          toast.success("Menu item reordered");
          return {
            ...prev,
            navItems: arrayMove(navItems, activeIndex, overIndex),
          };
        }
        return prev;
      });
    }
  };

  const handleImageUpload = (
    fieldName: "loaderImage" | "logo" | "favicon",
    url: string,
  ) => {
    setUploadedImages((prev) => ({
      ...prev,
      [fieldName]: url,
    }));
  };

  const openFileUpload = () => {
    fileInputRef.current?.click();
  };

  const processImageFile = (file: File) => {
    // Validate file type (PNG and SVG only)
    if (
      !["image/png", "image/svg+xml", "image/x-svg+xml"].includes(file.type)
    ) {
      toast.error("Only PNG and SVG formats are allowed");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File size must be less than 2MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64Data = event.target?.result as string;
      const fieldName =
        activeAssetTab === "loader" ? "loaderImage" : activeAssetTab;
      handleImageUpload(
        fieldName as "loaderImage" | "logo" | "favicon",
        base64Data,
      );
      const tabName =
        activeAssetTab.charAt(0).toUpperCase() + activeAssetTab.slice(1);
      toast.success(`${tabName} image uploaded!`);
    };
    reader.onerror = () => {
      toast.error("Failed to read file");
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processImageFile(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    processImageFile(file);
  };

  const handleRemoveImage = () => {
    const tabName =
      activeAssetTab.charAt(0).toUpperCase() + activeAssetTab.slice(1);
    const fieldName =
      activeAssetTab === "loader" ? "loaderImage" : activeAssetTab;
    setUploadedImages((prev) => ({
      ...prev,
      [fieldName]: undefined,
    }));
    toast.success(`${tabName} image removed`);
  };

  const getImageUrl = () => {
    const fieldName =
      activeAssetTab === "loader" ? "loaderImage" : activeAssetTab;
    return uploadedImages[fieldName as "loaderImage" | "logo" | "favicon"];
  };

  const handleReset = () => {
    setForm(originalForm);
    setUploadedImages(originalImages);
    toast.info("Changes reset to last saved state");
  };

  const handleSave = async () => {
    if (!branding) {
      toast.error("Failed to save: Branding data not loaded");
      return;
    }

    // Validate required fields
    if (!form.siteName || form.siteName.trim() === "") {
      toast.error("Site name is required");
      return;
    }

    // Store previous state for rollback
    const previousForm = { ...originalForm };
    const previousImages = { ...originalImages };

    // Optimistic update - update original state immediately
    setOriginalForm({ ...form });
    setOriginalImages({ ...uploadedImages });

    try {
      setSaving(true);

      const payload = {
        siteName: form.siteName || "",
        tagline: form.tagline || "",
        navItems: form.navItems || [],
        loaderImage: uploadedImages.loaderImage || "",
        logo: uploadedImages.logo || "",
        favicon: uploadedImages.favicon || "",
      };

      await axios.put(
        BASE_URL + `/api/site-branding/${branding._id}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      toast.success("Site branding updated successfully!");
    } catch (err: any) {
      // Revert on error
      setOriginalForm(previousForm);
      setOriginalImages(previousImages);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to update site branding";
      console.error("Save error:", err);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Theme Management Functions
  const fetchUserThemes = async (silent = false) => {
    try {
      if (!silent) setThemesLoading(true);
      const [res, settingsRes] = await Promise.all([
        axios.get(BASE_URL + "/api/user-theme"),
        axios.get("/api/domain/settings/global").catch(() => null),
      ]);
      setUserThemes(res.data);
      const freeIds = [
        ...(settingsRes?.data?.defaultFreeThemes || []).map((id: any) => id.toString()),
        ...(settingsRes?.data?.defaultFreeTheme ? [settingsRes.data.defaultFreeTheme.toString()] : []),
      ].filter((id, i, arr) => arr.indexOf(id) === i);
      if (freeIds.length > 0) setDefaultFreeThemeIds(freeIds);
    } catch (err: any) {
      console.error("Fetch user themes error:", err);
      if (err.response?.status !== 404) {
        toast.error(err.response?.data?.message || "Failed to load themes");
      }
    } finally {
      setThemesLoading(false);
    }
  };

  const handleActivateTheme = async (themeId: string) => {
    // Find the theme being activated
    const themeIndex = userThemes.findIndex((item) => item._id === themeId);
    const canUseTheme = themeIndex >= 0 && isItemVisibleOnWebsite('themes', themeIndex);

    if (!canUseTheme) {
      setShowUpgradeModal(true);
      toast.error("This theme is outside your plan limit");
      return;
    }

    // Optimistic update
    const previousThemes = [...userThemes];
    setActivatingThemeId(themeId);

    // Update UI optimistically
    setUserThemes((prev) =>
      prev.map((theme) => ({
        ...theme,
        isActive: theme._id === themeId,
      })),
    );

    // Follow the card to its new position at the top of the grid
    scrollToThemeCard(themeId);

    try {
      await axios.put(BASE_URL + `/api/user-theme/${themeId}/activate`);
      toast.success("Theme activated successfully!");
      // Clear preview theme if active — suppress the reset postMessage to avoid a flash
      if (previewContext?.previewTheme) {
        previewContext.clearPreviewAfterActivation();
      }
    } catch (err: any) {
      // Revert on error
      setUserThemes(previousThemes);
      console.error("Activate theme error:", err);
      toast.error(err.response?.data?.message || "Failed to activate theme");
    } finally {
      setActivatingThemeId(null);
    }
  };

  // Filter themes based on search
  const anyThemeActive = userThemes.some((ut) => ut.isActive);
  const filteredThemes = userThemes
    .map((ut) => ({
      ...ut.masterTheme,
      userThemeId: ut._id,
      // Use the UserTheme's own name (covers forks/variants); fall back to masterTheme title
      title: ut.themeName || ut.masterTheme?.title,
      isVariant: ut.themeName !== ut.masterTheme?.title,
      masterThemeTitle: ut.masterTheme?.title,
      isActive: ut.isActive || (!anyThemeActive && ut.masterTheme?._id === defaultFreeThemeIds[0]),
      order: ut.order,
      isDefault: defaultFreeThemeIds.includes(ut.masterTheme?._id || ''),
      isCustomized: !!(ut.customFonts && Object.keys(ut.customFonts).length > 0) || !!(ut.customColors && Object.keys(ut.customColors).length > 0),
    }))
    .filter((theme) =>
      theme.title?.toLowerCase().includes(themeSearchQuery.toLowerCase()),
    )
    .sort((a, b) => {
      // Active theme always first, then by order
      if (a.isActive && !b.isActive) return -1;
      if (!a.isActive && b.isActive) return 1;
      return (a.order ?? 0) - (b.order ?? 0);
    });

  if (loading) {
    return <PageLoader label="Loading site branding..." />;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="page-title">Website Settings</h2>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value: string) =>
          setActiveTab(value as "branding" | "navigation" | "theme" | "layout" | "cursor" | "customDomain")
        }
      >
        <TabsList>
          <TabsTrigger value="branding">Branding</TabsTrigger>
          {/* <TabsTrigger value="navigation">Navigation & Sections</TabsTrigger> */}
          <TabsTrigger value="theme">Theme</TabsTrigger>
          <TabsTrigger value="layout" className="gap-1.5">
            Layout
            {!capabilities.websiteLayoutsAccess && <Lock className="size-3.5" aria-label="Pro Max required" />}
          </TabsTrigger>
          <TabsTrigger value="cursor">Cursor</TabsTrigger>
          <TabsTrigger value="customDomain">Custom Domain</TabsTrigger>
        </TabsList>

        <TabsContent value="branding" className="mt-4">
          <div className="space-y-6 max-w-2xl">
            {/* Basic Information */}
            <div className="bg-card rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-section-title mb-4">Basic Information</h3>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="siteName">Site Name</Label>
                  <Input
                    id="siteName"
                    name="siteName"
                    type="text"
                    value={form.siteName}
                    onChange={handleFormChange}
                    placeholder="Artist Name or Band Name"
                  />
                </div>
                {/* <div className="space-y-1.5">
                                    <Label htmlFor="tagline">Tagline</Label>
                                    <Input
                                        id="tagline"
                                        name="tagline"
                                        type="text"
                                        value={form.tagline}
                                        onChange={handleFormChange}
                                        placeholder="Enter tagline"
                                    />
                                </div> */}
              </div>
            </div>

            {/* Branding Assets */}
            <div className="bg-card rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-section-title mb-4">Branding Assets</h3>
              <Tabs
                value={activeAssetTab}
                onValueChange={(value: string) =>
                  setActiveAssetTab(value as "loader" | "logo" | "favicon")
                }
              >
                <TabsList className="w-full">
                  <TabsTrigger value="loader" className="flex-1">
                    Loader
                  </TabsTrigger>
                  <TabsTrigger value="logo" className="flex-1">
                    Logo
                  </TabsTrigger>
                  <TabsTrigger value="favicon" className="flex-1">
                    Favicon
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="loader" className="mt-4">
                  {!getImageUrl() ? (
                    <div className="w-full">
                      <Card
                        className={`group flex max-h-[200px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer transition-colors ${isDragActive ? "border-primary bg-muted/50" : "hover:bg-muted/50"}`}
                        onClick={openFileUpload}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        <div className="grid space-y-3">
                          <div className="flex items-center gap-x-2 text-muted-foreground justify-center">
                            <Upload className="size-5" />
                            <div>
                              Drop image here or{" "}
                              <Button
                                type="button"
                                variant="link"
                                className="text-primary p-0 h-auto font-normal"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openFileUpload();
                                }}
                              >
                                browse
                              </Button>{" "}
                              to upload
                            </div>
                          </div>
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          accept=".png,.svg"
                          onChange={handleFileChange}
                        />
                        <span className="text-xs text-muted-foreground mt-1 block">
                          Supported: PNG, SVG (max 2 MB)
                        </span>
                      </Card>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Card
                        className={`group flex max-h-[400px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer transition-colors ${isDragActive ? "border-primary bg-muted/50" : "hover:bg-muted/50"}`}
                        onClick={openFileUpload}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        <div className="relative max-h-64 w-full sm:max-w-64 rounded overflow-hidden">
                          <img
                            src={getImageUrl()}
                            alt="loader"
                            className="w-full h-full object-contain cursor-pointer hover:opacity-80 transition-opacity"
                            title="Click to edit image"
                          />
                        </div>
                        <div className="flex items-center gap-x-2 text-muted-foreground text-xs">
                          <Upload className="size-4" />
                          <span>Click to edit or re-upload</span>
                        </div>
                      </Card>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-foreground"
                          onClick={openFileUpload}
                        >
                          <RefreshCw className="size-3.5" />
                          Change
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-danger"
                          onClick={handleRemoveImage}
                        >
                          <Trash2 className="size-3.5" />
                          Remove
                        </Button>
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="logo" className="mt-4">
                  {!getImageUrl() ? (
                    <div className="w-full">
                      <Card
                        className={`group flex max-h-[200px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer transition-colors ${isDragActive ? "border-primary bg-muted/50" : "hover:bg-muted/50"}`}
                        onClick={openFileUpload}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        <div className="grid space-y-3">
                          <div className="flex items-center gap-x-2 text-muted-foreground justify-center">
                            <Upload className="size-5" />
                            <div>
                              Drop image here or{" "}
                              <Button
                                type="button"
                                variant="link"
                                className="text-primary p-0 h-auto font-normal"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openFileUpload();
                                }}
                              >
                                browse
                              </Button>{" "}
                              to upload
                            </div>
                          </div>
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          accept=".png,.svg"
                          onChange={handleFileChange}
                        />
                        <span className="text-xs text-muted-foreground mt-1 block">
                          Supported: PNG, SVG (max 2 MB)
                        </span>
                      </Card>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Card
                        className={`group flex max-h-[400px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer transition-colors ${isDragActive ? "border-primary bg-muted/50" : "hover:bg-muted/50"}`}
                        onClick={openFileUpload}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        <div className="relative max-h-64 w-full sm:max-w-64 rounded overflow-hidden">
                          <img
                            src={getImageUrl()}
                            alt="logo"
                            className="w-full h-full object-contain cursor-pointer hover:opacity-80 transition-opacity"
                            title="Click to edit image"
                          />
                        </div>
                        <div className="flex items-center gap-x-2 text-muted-foreground text-xs">
                          <Upload className="size-4" />
                          <span>Click to edit or re-upload</span>
                        </div>
                      </Card>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-foreground"
                          onClick={openFileUpload}
                        >
                          <RefreshCw className="size-3.5" />
                          Change
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-danger"
                          onClick={handleRemoveImage}
                        >
                          <Trash2 className="size-3.5" />
                          Remove
                        </Button>
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="favicon" className="mt-4">
                  {!getImageUrl() ? (
                    <div className="w-full">
                      <Card
                        className={`group flex max-h-[200px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer transition-colors ${isDragActive ? "border-primary bg-muted/50" : "hover:bg-muted/50"}`}
                        onClick={openFileUpload}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        <div className="grid space-y-3">
                          <div className="flex items-center gap-x-2 text-muted-foreground justify-center">
                            <Upload className="size-5" />
                            <div>
                              Drop image here or{" "}
                              <Button
                                type="button"
                                variant="link"
                                className="text-primary p-0 h-auto font-normal"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openFileUpload();
                                }}
                              >
                                browse
                              </Button>{" "}
                              to upload
                            </div>
                          </div>
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          accept=".png,.svg"
                          onChange={handleFileChange}
                        />
                        <span className="text-xs text-muted-foreground mt-1 block">
                          Supported: PNG, SVG (max 2 MB)
                        </span>
                      </Card>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Card
                        className={`group flex max-h-[400px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer transition-colors ${isDragActive ? "border-primary bg-muted/50" : "hover:bg-muted/50"}`}
                        onClick={openFileUpload}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        <div className="relative max-h-64 w-full sm:max-w-64 rounded overflow-hidden">
                          <img
                            src={getImageUrl()}
                            alt="favicon"
                            className="w-full h-full object-contain cursor-pointer hover:opacity-80 transition-opacity"
                            title="Click to edit image"
                          />
                        </div>
                        <div className="flex items-center gap-x-2 text-muted-foreground text-xs">
                          <Upload className="size-4" />
                          <span>Click to edit or re-upload</span>
                        </div>
                      </Card>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-foreground"
                          onClick={openFileUpload}
                        >
                          <RefreshCw className="size-3.5" />
                          Change
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-danger"
                          onClick={handleRemoveImage}
                        >
                          <Trash2 className="size-3.5" />
                          Remove
                        </Button>
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>

            {/* Status & Actions */}
            <div className="bg-card rounded-lg shadow p-4 sm:p-6">
              <div className="flex gap-2">
                <Button
                  onClick={handleSave}
                  disabled={!hasChanges}
                  loading={saving}
                  loadingText="Saving..."
                >
                  Save Changes
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReset}
                  disabled={saving || !hasChanges}
                >
                  Reset
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="navigation" className="mt-4">
          <div className="space-y-6 w-full">
            {/* Navigation Bar Settings */}
            <div className="bg-card rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-section-title mb-4">
                Navigation & Sections Settings
              </h3>
              <div className="space-y-3">
                {/* Column Headers */}
                {(form.navItems || []).length > 0 && (
                  <div className="hidden sm:flex gap-3 items-center py-2 px-2 text-xs font-medium text-muted-foreground">
                    <span className="w-4"></span>
                    <span className="w-5">#</span>
                    <span className="flex-1">Name</span>
                    <span className="flex-1">Section id</span>
                  </div>
                )}
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={(form.navItems || []).map((_, i) => `nav-${i}`)}
                    strategy={verticalListSortingStrategy}
                  >
                    {(form.navItems || []).map((item, index) => (
                        <SortableNavItem
                          key={`nav-${index}`}
                          id={`nav-${index}`}
                          index={index}
                          item={item}
                          onNameChange={(value) =>
                            handleNavItemChange(index, "name", value)
                          }
                          onHrefChange={(value) =>
                            handleNavItemChange(index, "href", value)
                          }
                          readOnly={!isSuperAdmin}
                        />
                    ))}
                  </SortableContext>
                </DndContext>
                {(form.navItems || []).length === 0 && (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    No menu items yet. Add your first menu item below.
                  </div>
                )}
              </div>
            </div>

            {/* Status & Actions */}
            <div className="bg-card rounded-lg shadow p-4 sm:p-6">
              <div className="flex gap-2">
                <Button
                  onClick={handleSave}
                  disabled={!hasChanges}
                  loading={saving}
                  loadingText="Saving..."
                >
                  Save Changes
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReset}
                  disabled={saving || !hasChanges}
                >
                  Reset
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="theme" className="mt-4">
          <div className="space-y-6">
            {/* Header with Search */}
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground size-4" />
              <Input
                type="text"
                placeholder="Search themes..."
                value={themeSearchQuery}
                onChange={(e) => setThemeSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* All Themes Grid */}
            {themesLoading ? (
              <div className={`grid grid-cols-2 gap-3 sm:gap-6 ${!isPreviewOpen ? 'lg:grid-cols-3' : ''}`}>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="rounded-lg overflow-hidden border border-border animate-pulse">
                    <div className="aspect-video bg-muted" />
                    <div className="p-4 space-y-3">
                      <div className="h-5 bg-muted rounded w-3/4" />
                      <div className="flex gap-2">
                        <div className="h-8 bg-muted rounded flex-1 hidden sm:block" />
                        <div className="h-8 bg-muted rounded flex-1" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredThemes.length === 0 ? (
              <div className="bg-card rounded-lg shadow p-12 text-center">
                <div className="text-muted-foreground space-y-2">
                  <p className="text-base sm:text-lg font-medium">No themes found</p>
                  {themeSearchQuery ? (
                    <p className="text-sm">Try a different search term</p>
                  ) : (
                    <p className="text-sm">
                      No themes have been allocated to your domain yet. Please
                      contact admin.
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className={`grid grid-cols-2 gap-3 sm:gap-6 ${!isPreviewOpen ? 'lg:grid-cols-3' : ''}`}>
                {filteredThemes.map((theme) => {
                  const themeIndex = userThemes.findIndex((item) => item._id === theme.userThemeId);
                  const canUseTheme = themeIndex >= 0 && isItemVisibleOnWebsite('themes', themeIndex);
                  return (
                  <Card
                    key={theme.userThemeId}
                    ref={(el: HTMLDivElement | null) => {
                      if (el) themeCardRefs.current.set(theme.userThemeId!, el);
                      else themeCardRefs.current.delete(theme.userThemeId!);
                    }}
                    className={`overflow-hidden transition-all duration-200 ${
                      theme.isActive
                        ? "ring-2 ring-primary shadow-lg"
                        : "hover:shadow-none"
                    }`}
                    style={{ contentVisibility: 'auto', containIntrinsicSize: '0 320px' }}
                  >
                    {/* Theme Preview Image — clickable to activate. Mirrors the
                        onboarding pattern: many users instinctively tap the
                        thumbnail rather than the small button below, so the
                        whole image is a tap target. The Activate button below
                        is kept as a redundant explicit affordance. */}
                    <div
                      className="relative aspect-video bg-muted overflow-hidden group/thumb cursor-pointer"
                      role="button"
                      tabIndex={0}
                      aria-label={
                        !canUseTheme
                          ? `Unlock ${theme.title}`
                          : `Activate ${theme.title}`
                      }
                      onClick={() => {
                        if (theme.isActive) return;
                        if (!canUseTheme) {
                          setShowUpgradeModal(true);
                        } else {
                          handleActivateTheme(theme.userThemeId!);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          if (theme.isActive) return;
                          if (!canUseTheme) {
                            setShowUpgradeModal(true);
                          } else {
                            handleActivateTheme(theme.userThemeId!);
                          }
                        }
                      }}
                    >
                      {theme.image ? (
                        <img
                          src={theme.image}
                          alt={theme.title}
                          className="w-full h-full object-cover transition-transform duration-200 group-hover/thumb:scale-[1.03]"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                          <span className="text-sm">No preview</span>
                        </div>
                      )}
                      {/* Hover affordance — shows what tapping will do */}
                      {canUseTheme && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover/thumb:bg-black/30 transition-colors duration-200 pointer-events-none">
                          <span className="opacity-0 group-hover/thumb:opacity-100 transition-opacity duration-200 text-white text-xs font-medium bg-black/60 px-2.5 py-1 rounded-md flex items-center gap-1">
                            <Check className="size-3" />
                            {theme.isActive ? "Already active" : "Tap to activate"}
                          </span>
                        </div>
                      )}
                      {/* Locked overlay for themes outside the current plan limit */}
                      {!canUseTheme && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <div className="bg-black/70 text-white px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5">
                            <Lock className="size-3.5" />
                            Upgrade to unlock
                          </div>
                        </div>
                      )}
                      {theme.isActive && (
                        <div className="absolute top-2 right-2 bg-primary text-primary-foreground px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1">
                          <Check className="size-3" />
                          Active
                        </div>
                      )}
                      {isPro && canUseTheme && !theme.isActive && (
                        <div className="absolute top-2 right-2 bg-primary text-primary-foreground px-2 py-1 rounded-md text-xs font-medium">
                          Pro
                        </div>
                      )}
                      {theme.isCustomized && (
                        <div className="absolute top-2 left-2 bg-primary-light text-black px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1">
                          <Palette className="size-3" />
                          Customized
                        </div>
                      )}
                    </div>

                    {/* Theme Info */}
                    <div className="p-4 space-y-3">
                      <div>
                        <h3 className="font-semibold text-base truncate">
                          {theme.title}
                        </h3>
                        {theme.isVariant && (
                          <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                            Based on {theme.masterThemeTitle}
                          </p>
                        )}
                      </div>

                      {/* Action Buttons — icon-only, 3-col. Always fits regardless of card width. */}
                      <div className="grid grid-cols-3 gap-1.5">

                        {/* Preview */}
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          title="Preview theme"
                          onClick={() => {
                            previewContext?.setPreviewTheme({
                              themeTitle: theme.title,
                              userThemeId: theme.userThemeId!,
                              isDefault: theme.isDefault,
                            });
                            previewContext?.triggerOpenPanel();
                          }}
                        >
                          <Eye className="size-4" />
                        </Button>

                        {/* Customize — plan gated on non-default themes for free users */}
                        {!canUseTheme ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full"
                            title="Upgrade to customize"
                            onClick={() => setShowUpgradeModal(true)}
                          >
                            <Lock className="size-4" />
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full"
                            title="Customize fonts & colors"
                            onClick={() => {
                              const ut = userThemes.find(t => t._id === theme.userThemeId);
                              setCustomizerTarget({
                                userThemeId: theme.userThemeId!,
                                themeTitle: theme.title,
                                isVariant: theme.isVariant,
                                masterThemeTitle: theme.masterThemeTitle || theme.title,
                                savedFonts: ut?.customFonts
                                  ? Object.fromEntries((ut.customFonts as any) instanceof Map
                                      ? (ut.customFonts as any)
                                      : Object.entries(ut.customFonts))
                                  : {},
                                savedColors: ut?.customColors
                                  ? Object.fromEntries((ut.customColors as any) instanceof Map
                                      ? (ut.customColors as any)
                                      : Object.entries(ut.customColors))
                                  : {},
                                savedFontWeight: ut?.customFontWeight ?? null,
                savedBackgroundImage: ut?.customBackgroundImage ?? null,
                                savedSections: ut?.customSections
                                  ? (ut.customSections as any) instanceof Map
                                    ? Object.fromEntries(ut.customSections as any)
                                    : (ut.customSections as Record<string, Record<string, string>>)
                                  : {},
                                savedSectionBackgrounds: ut?.customSectionBackgrounds
                                  ? (ut.customSectionBackgrounds as any) instanceof Map
                                    ? Object.fromEntries(ut.customSectionBackgrounds as any)
                                    : (ut.customSectionBackgrounds as Record<string, { image?: string | null; video?: string | null; opacity?: number }>)
                                  : {},
                              });
                              setCustomizerOpen(true);
                            }}
                          >
                            <Palette className="size-4" />
                          </Button>
                        )}

                        {/* Activate / Active / Upgrade */}
                        {!theme.isActive ? (
                          !canUseTheme ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full"
                              title="Upgrade to unlock"
                              onClick={() => setShowUpgradeModal(true)}
                            >
                              <Lock className="size-4" />
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              className="w-full"
                              title="Activate theme"
                              onClick={() => handleActivateTheme(theme.userThemeId!)}
                              loading={activatingThemeId === theme.userThemeId}
                              loadingText="..."
                            >
                              <Zap className="size-4" />
                            </Button>
                          )
                        ) : (
                          <Button
                            size="sm"
                            variant="secondary"
                            className="w-full"
                            title="Currently active"
                            disabled
                          >
                            <Check className="size-4" />
                          </Button>
                        )}

                      </div>
                    </div>
                  </Card>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="layout" className="mt-4">
          <WebsiteLayoutEditor domain={domain} />
        </TabsContent>

        <TabsContent value="cursor" className="mt-4">
          <CursorControlPanel domain={domain} />
        </TabsContent>

        <TabsContent value="customDomain" className="mt-4">
          {domainObj && domainObj._id ? (
            <CustomDomainSettings
              domainId={domainObj._id}
              slug={domainObj.slug}
              initialCustomDomain={domainObj.customDomain ?? null}
              initialDnsVerified={domainObj.dnsVerified ?? false}
              onChange={(next) => _setDomain((prev: Domain) => ({ ...prev, customDomain: next ?? undefined, dnsVerified: false } as Domain))}
              onVerified={() => _setDomain((prev: Domain) => ({ ...prev, dnsVerified: true } as Domain))}
            />
          ) : (
            <div className="text-sm text-muted-foreground">Select a domain to manage its custom domain.</div>
          )}
        </TabsContent>
      </Tabs>

      {/* Theme Customizer Drawer — active theme */}
      {customizerTarget && (
        <ThemeCustomizerDrawer
          open={customizerOpen}
          onClose={() => setCustomizerOpen(false)}
          userThemeId={customizerTarget.userThemeId}
          themeTitle={customizerTarget.themeTitle}
          isVariant={customizerTarget.isVariant}
          masterThemeTitle={customizerTarget.masterThemeTitle}
          siteType={domainObj.siteType}
          siteName={branding?.siteName || ''}
          savedFonts={customizerTarget.savedFonts}
          savedColors={customizerTarget.savedColors}
          savedFontWeight={customizerTarget.savedFontWeight}
          savedBackgroundImage={customizerTarget.savedBackgroundImage}
          savedSections={customizerTarget.savedSections}
          savedSectionBackgrounds={customizerTarget.savedSectionBackgrounds}
          onSaved={() => fetchUserThemes(true)}
        />
      )}
    </div>
  );
};

export default SiteBrandingManager;
